/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, useCallback, useEffect, useRef, useState} from 'react';
import {loadModules} from 'esri-loader';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {HVSRecord} from '@ion/core/src/types/entities';
import {SystemOfUnitsMap} from '@ion/core/src/types/general';
import {useIsDesktop, useMedia} from '@ion/dls/src/hooks/useMedia';
import {CreateGeofences} from '@ion/dls/src/maps/geofences/CreateGeofences';
import {BoundaryPanelProps} from '@ion/dls/src/maps/types/types';
import {addComponent, removeComponent} from '../helpers/simpleMapUtils';
import {timeSliderTrigger} from '../helpers/TimeSliderTrigger';
import {BasemapGalleryTool} from '../tools/BasemapGalleryTool';
import {CreateGeofenceToolFactory} from '../tools/CreateGeofencesTool';
import {FullScreenToolFactory} from '../tools/FullScreenTool';
import HomeToolFactory from '../tools/HomeTool';
import {LayerChooserToolFactory} from '../tools/LayerChooserTool';
import {LocationToolFactory} from '../tools/LocationTool';
import {MoreMenuTool} from '../tools/MoreMenuTool';
import {RefreshToolFactory} from '../tools/RefreshTool';
import {SavedMapViewToolFactory} from '../tools/SavedMapViewTool';
import {SearchMapToolFactory} from '../tools/SearchMapTool';
import {Timeline, TimeSliderTool} from '../tools/TimeSliderTool';
import {ZoomInOutTool} from '../tools/ZoomInOutTool';
import {
  BasemapLayerTool,
  BasemapTypes,
  MapToolPositions,
  MapTools
} from '../types/types';

// Defines the props for the BasemapLayer component.
export type BasemapLayerProps = {
  map: __esri.Map;
  view: __esri.MapView;
  basemap: BasemapTypes;
  createOSMBasemap: Function;
  tools?: BasemapLayerTool[];
  timeline?: Timeline;
  timeSliderDisabled?: boolean;
  assetsHistory?: any[];
  boundaryPanelProps?: BoundaryPanelProps;
  currentAccountId?: number;
  open?: boolean;
  shouldShowTooltip?: boolean;
  BasemapLayerProps?: boolean;
  isVertical?: boolean;
  systemOfUnits?: string;
  usePortal?: boolean;
  hoveredEvent?: HVSRecord;
  MapControlsBreadCrumbs?: null | ReactElement;
};

// Unique identifier for the basemap layer.
export const BASEMAP_LAYER_ID = 'map-layer-basemap';

const noop = function() {};

// Creates a map Scale Bar tool.
const scaleBarToolFactory = ({
  position,
  systemOfUnits,
  view,
  index
}) => {
  const {
    value: userUOM = SystemOfUnitsMap.Metric
  } = systemOfUnits || {};

  loadModules(['esri/widgets/ScaleBar'])
    .then(([ScaleBar]) => {
      const scaleBar = new ScaleBar({
        id    : 'scaleBar',
        unit  : SystemOfUnitsMap[userUOM],
        style : 'line',
        view
      });
      view.ui.add(scaleBar, {position, index});
    })
    .catch((err) => error(err));
};

// Creates a map Zoom In/Out tool.
const zoomInOutToolFactory = ({
  view,
  isVertical
}) => (
  <ZoomInOutTool onZoomOut={function() {
    const {zoom} = view;
    view.goTo({zoom: zoom - 1});
  }} onZoomIn={function() {
    const {zoom} = view;
    view.goTo({zoom: zoom + 1});
  }}
  isVertical = {isVertical}/>
);

// Creates a map Timeslider tool.
const timeSliderToolFactory = ({
  onToggleTimeSlider,
  timeSliderDisabled
}) => (
  <TimeSliderTool onToggleTimeSlider={onToggleTimeSlider}
    timeSliderDisabled={timeSliderDisabled}/>
);

// Creates a map Basemap Gallery tool.
export const basemapGalleryToolFactory = ({
  map,
  view,
  createOSMBasemap,
  usePortal,
  onBasemapChanged,
  activeTool,
  setActiveTool,
  isAdminGeofence = false
}) => (
  <BasemapGalleryTool map={map}
    view={view}
    usePortal={usePortal}
    createOSMBasemap={createOSMBasemap}
    onBasemapChanged={onBasemapChanged}
    activeTool={activeTool}
    setActiveTool={setActiveTool}
    isAdminGeofence={isAdminGeofence} />
);

// Factory functions to create tools on the Basemap layer.
const toolFactories = {
  [MapTools.zoomInOut] : (args) => {
    const {
      tool: {
        id,
        position
      },
      view,
      index,
      isVertical
    } = args;

    const component = zoomInOutToolFactory({view, isVertical});
    addComponent({
      component,
      id,
      position,
      view,
      index
    });
  },

  [MapTools.timeSlider] : (args) => {
    const {
      tool: {
        id,
        position,
        props: {
          onToggleTimeSlider
        }
      },
      timeSliderDisabled,
      view,
      index
    } = args;

    const component = timeSliderToolFactory({
      onToggleTimeSlider,
      timeSliderDisabled
    });
    addComponent({
      component,
      id,
      position,
      view,
      index
    });
  },

  [MapTools.refreshMap] : (args) => {
    const {
      tool: {
        props : {
          onRefresh
        },
        id,
        position
      },
      view,
      index
    } = args;

    const component = RefreshToolFactory({onRefresh});
    addComponent({
      component,
      id,
      position,
      view,
      index
    });
  },

  [MapTools.scaleBar] : (args) => {
    const {
      tool: {
        position,
        props: {
          systemOfUnits
        }
      },
      view,
      index
    } = args;
    scaleBarToolFactory({position, systemOfUnits, view, index});
  },

  [MapTools.fullScreen] : (args) => {
    const {
      tool: {
        position
      },
      view,
      index
    } = args;
    addComponent({
      component : <FullScreenToolFactory view={view} />,
      id        : MapTools.fullScreen,
      position,
      view,
      index
    });
  },

  [MapTools.goToHome] : (args) => {
    const {
      tool: {
        props: {
          isDisableInitialExtent,
          initialExtent,
          callBack
        },
        id,
        position
      },
      index,
      view,
      map,
      basemapLayerRef
    } = args;
    addComponent({
      component : (
        <HomeToolFactory map={map}
          view={view}
          isDisableInitialExtent={isDisableInitialExtent}
          initialExtent={initialExtent}
          callBack={callBack}
          layerRef={basemapLayerRef} />
      ),
      id,
      position,
      view,
      index
    });
  },

  [MapTools.location] : (args) => {
    const {
      tool: {
        id,
        position
      },
      map,
      view,
      index
    } = args;
    addComponent({
      component : (
        <LocationToolFactory view={view} map={map} />
      ),
      id,
      position,
      view,
      index
    });
  },

  [MapTools.searchMap] : (args) => {
    const {
      tool: {
        id,
        position,
        props: {
          savedSearchHistoryItems,
          userPreferenceName,
          searchResult,
          onCloseSearchPopup,
          setSelectedTrackingSearch,
          useHereApi = false
        }
      },
      index,
      view,
      activeTool,
      setActiveTool,
      MapControlsBreadCrumbs
    } = args;
    addComponent({
      component : (
        <SearchMapToolFactory view={view}
          position={position}
          activeTool={activeTool}
          useHereApi={useHereApi}
          searchResult={searchResult}
          onCloseSearchPopup={onCloseSearchPopup}
          setSelectedTrackingSearch={setSelectedTrackingSearch}
          setActiveTool={setActiveTool}
          userPreferenceName={userPreferenceName}
          savedSearchHistoryItems={savedSearchHistoryItems}
          MapControlsBreadCrumbs={MapControlsBreadCrumbs} />
      ),
      id,
      position,
      view,
      index
    });
  },

  [MapTools.createGeoFence] : (args) => {
    const {
      tool: {
        id,
        position,
        props: {
          useHereApi = false,
          geofenceSave,
          savingGeofence,
          toggleOpenClose,
          createPopupStyle,
          isAdminGeofence,
          globalGroup,
          currentAccountId,
          isShowViewAllOption,
          editGeofenceGraphic,
          setEditGeofenceGraphic,
          accountMap,
          open
        }
      },
      index,
      view,
      map,
      activeTool,
      setActiveTool
    } = args;
    addComponent({
      component : (
        <CreateGeofenceToolFactory view={view}
          accountMap={accountMap}
          globalGroup={globalGroup}
          geofenceSave={geofenceSave}
          savingGeofence={savingGeofence}
          position={position}
          open={!!open}
          useHereApi={useHereApi}
          createPopupStyle={createPopupStyle}
          currentAccountId={currentAccountId}
          editGeofenceGraphic={editGeofenceGraphic}
          isAdminGeofence={isAdminGeofence}
          isShowViewAllOption={isShowViewAllOption}
          setEditGeofenceGraphic={setEditGeofenceGraphic}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          toggleOpenClose={toggleOpenClose}
          map={map} />
      ),
      id,
      position,
      view,
      index
    });
  },

  [MapTools.moreMenu] : (args) => {
    const {
      tool: {
        id,
        position,
        props: {
          currentAccountId,
          editGeofenceGraphic,
          shouldShowTooltip,
          globalGroup,
          accountMap
        }
      },
      index,
      map,
      view,
      createOSMBasemap,
      basemapLayerRef,
      activeTool,
      setActiveTool,
      tools
    } = args;
    addComponent({
      component : (
        <MoreMenuTool tools={tools}
          view={view}
          map={map}
          createOSMBasemap={createOSMBasemap}
          accountMap={accountMap}
          globalGroup={globalGroup}
          currentAccountId={currentAccountId}
          basemapLayerRef={basemapLayerRef}
          shouldShowTooltip={shouldShowTooltip}
          editGeofenceGraphic={editGeofenceGraphic} />
      ),
      id,
      position,
      view,
      index
    });
  },

  [MapTools.layerChooser] : (args) => {
    const {
      tool: {
        id,
        position,
        props: {
          open,
          onClose,
          onLayersChange,
          selectedLayers,
          user,
          accounts,
          mapLayerSettings,
          trafficLayerSettingProps,
          geofenceLayerSettingProps,
          weatherLayerSettingProps,
          currentLayerSettingProps,
          operationLayerSettingProps,
          tripLayerSettingProps
        }
      },
      index,
      view,
      map,
      activeTool,
      setActiveTool
    } = args;
    addComponent({
      component : (
        <LayerChooserToolFactory view={view}
          map={map}
          user={user}
          accounts={accounts}
          onClose={onClose}
          onLayersChange={onLayersChange}
          selectedLayers={selectedLayers}
          open={open}
          position={position}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          mapLayerSettings={mapLayerSettings}
          trafficLayerSettingProps={trafficLayerSettingProps}
          geofenceLayerSettingProps={geofenceLayerSettingProps}
          weatherLayerSettingProps={weatherLayerSettingProps}
          currentLayerSettingProps={currentLayerSettingProps}
          operationLayerSettingProps={operationLayerSettingProps}
          tripLayerSettingProps={tripLayerSettingProps} />
      ),
      id,
      position,
      view,
      index
    });
  },

  [MapTools.basemapGallery] : (args) => {
    const {
      tool : {
        id,
        position,
        props : {
          onBasemapChanged,
          isAdminGeofence
        }
      },
      index,
      map,
      view,
      createOSMBasemap,
      usePortal,
      activeTool,
      setActiveTool
    } = args;

    const component = basemapGalleryToolFactory({
      map,
      view,
      createOSMBasemap,
      usePortal,
      onBasemapChanged,
      activeTool,
      setActiveTool,
      isAdminGeofence
    });
    addComponent({
      component,
      id,
      position,
      view,
      index
    });
  },

  [MapTools.savedMap] : (args) => {
    const {
      tool: {
        id = undefined,
        position = MapToolPositions.topRight,
        props: {
          onSaveMapView = noop,
          savedMapViews = noop,
          deleteSavedMapView = noop,
          editSavedMapView = noop,
          setSavedMapView = noop,
          setDefaultMapView = noop
        } = {}
      } = {},
      index,
      view,
      activeTool,
      setActiveTool
    } = args;
    addComponent({
      component : (
        <SavedMapViewToolFactory view={view}
          position={position}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          onSaveMapView={onSaveMapView}
          savedMapViews={savedMapViews}
          deleteSavedMapView={deleteSavedMapView}
          editSavedMapView={editSavedMapView}
          setDefaultMapView={setDefaultMapView}
          setSavedMapView={setSavedMapView} />
      ),
      id,
      position,
      view,
      index
    });
  }
};

/**
 * Defines the Basemap layer of a SimpleMap.
 *
 * ```typescript
 *
 *   import BasemapLayer from '@ion/features/src/maps/layers/BasemapLayer';
 *   import {MapTools, MapToolPositions} from '@ion/features/src/maps/types/types';
 *
 *   const layers = [
 *   {
 *     id        : 'map-layer-basemap',
 *     component : BasemapLayer,
 *     props     : {
 *       tools : [
 *         {
 *           id       : MapTools.basemapGallery,
 *           position : MapToolPositions.topRight,
 *           props    : {
 *             onBasemapChanged : setBasemap
 *           }
 *         },
 *         {
 *           id       : MapTools.zoomInOut,
 *           position : MapToolPositions.bottomRight
 *         },
 *         {
 *           id       : MapTools.scaleBar,
 *           position : MapToolPositions.bottomRight,
 *           props    : {
 *             systemOfUnits
 *           }
 *         }
 *       ],
 *       basemap
 *     }
 *   }];
 *
 * ```
 */
export const BasemapLayer = ({
  map,
  view,
  basemap,
  createOSMBasemap,
  tools,
  timeline,
  systemOfUnits,
  currentAccountId,
  timeSliderDisabled,
  assetsHistory,
  hoveredEvent,
  boundaryPanelProps : {
    toggleShowBoundaryPanel,
    boundaryPanelOpen,
    selectedBoundaryTool,
    onApplyBoundary
  } = {
    toggleShowBoundaryPanel : null,
    boundaryPanelOpen       : false,
    selectedBoundaryTool    : null,
    onApplyBoundary         : null
  },
  open = false,
  usePortal = false,
  isVertical = true,
  MapControlsBreadCrumbs = null
}: BasemapLayerProps) => {
  const isDesktop = useIsDesktop();

  const basemapLayerRef             = useRef<__esri.Layer>(null);
  const [activeTool, setActiveTool] = useState(null);

  const [editGeofenceGraphic, setEditGeofenceGraphic] = useState(null);
  const geoFencesGraphicsLayerRef = useRef(null);
  const childRef                  = useRef(null);
  const sketchViewModelRef        = useRef(null);
  const selectedMapPointRef       = useRef(null);
  const {isPhone}                 = useMedia();

  // adding timeSlider component to the mapView
  useEffect(() => {
    const {assetId} = assetsHistory?.[0] || {};
    timeSliderTrigger({
      map,
      view,
      timeline,
      timeSliderDisabled,
      assetsHistory : hoveredEvent ? getSelectedTrip(hoveredEvent)
        : assetsHistory?.filter(({assetId: itemAssetId}) => assetId === itemAssetId)
    });
  }, [timeline, assetsHistory, hoveredEvent]);

  /*
 * getSelectedTrip function get the assetHistory based on hovered event passed,
 * if the individual individual asset event is selected get the list of entries for that asset event selected
 * otherwise if user selected a trip header get the complete list of event from the assetHistory.
 */
  const getSelectedTrip = useCallback((selectedEvent) => {
    const {
      id,
      tripGroup
    } = selectedEvent;
    return assetsHistory.filter(node => (
      tripGroup ? node?.tripGroup === tripGroup : node?.id === Number(id)
    ));
  }, [assetsHistory]);

  useEffect(() => {
    if (boundaryPanelOpen) {
      addComponent({
        id        : MapTools.mapCreateGeofence,
        position  : MapToolPositions.topRight,
        component : (
          <CreateGeofences map={map}
            mapView={view}
            usePortal={usePortal}
            isPhone={isPhone}
            toggleShowBoundaryPanel={toggleShowBoundaryPanel}
            isBoundaryCreation={true}
            selectedDrawingTool={selectedBoundaryTool}
            onApplyBoundary={onApplyBoundary}
            closeGeoFencePanel={toggleShowBoundaryPanel}
            editGeofenceGraphic={editGeofenceGraphic}
            setEditGeofenceGraphic={setEditGeofenceGraphic}
            geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
            rootTitle={translate('maps:boundary.createBoundary')}
            sketchViewModelRef={sketchViewModelRef}
            selectedMapPointRef={selectedMapPointRef}
            createPopupStyle={{
              top   : '24px',
              right : '0'
            }}
            show={boundaryPanelOpen}/>
        ),
        view
      });
    } else {
      removeComponent(MapTools.mapCreateGeofence, view);
    }
  }, [view, boundaryPanelOpen]);

  useEffect(() => {
    scaleBarToolFactory({
      view,
      systemOfUnits,
      index    : 1,
      position : MapToolPositions.bottomRight
    });
  }, []);

  useEffect(() => {
    loadModules(['esri/layers/VectorTileLayer'])
      .then(([VectorTileLayer]) => {
        if (!basemapLayerRef.current) {
          basemapLayerRef.current = new VectorTileLayer({
            id      : BASEMAP_LAYER_ID,
            title   : BASEMAP_LAYER_ID,
            visible : basemap === BasemapTypes.grayVector,
            url     : 'assets/maps/light-gray-canvas.json'
          });
          map.add(basemapLayerRef.current);
          map.layers.reorder(basemapLayerRef.current, 0);
        } else {
          basemapLayerRef.current.visible = basemap === BasemapTypes.grayVector;
        }
        tools.forEach((tool, index) => {
          const {isHideOnMobileView, isShowOnlyOnMobileView} = tool.props|| {};
          const isValidDesktop = (isUndefined(isHideOnMobileView) || isHideOnMobileView) && !isShowOnlyOnMobileView;
          const isValidMobile  = isShowOnlyOnMobileView || !isHideOnMobileView;
          const isValid = (isDesktop && isValidDesktop) || (isPhone && isValidMobile);

          if (isValid) {
            toolFactories[tool.id]({
              index,
              tool,
              map,
              view,
              basemapLayerRef,
              createOSMBasemap,
              activeTool,
              setActiveTool,
              timeSliderDisabled,
              tools,
              isVertical,
              usePortal,
              MapControlsBreadCrumbs
            });
          }
        });
      })
      .catch((err) => error(err));
  }, [
    map,
    view,
    basemap,
    tools,
    basemapLayerRef,
    isPhone,
    isDesktop,
    activeTool,
    currentAccountId,
    createOSMBasemap
  ]);

  return null;
};

export default BasemapLayer;
